import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Building2, Globe, LoaderCircle } from 'lucide-react'
import { api } from '../api'
import type { Brand, Workspace } from '../types'
import type { Task } from '../tasks'
import { go, setActiveWorkspaceRoute } from '../router'
import { Button, Card, CardTitle, ErrorText, Field, Input, Note } from '../components/ui'

type Created = { workspace: Workspace; brand?: Brand; task?: Task }

const websiteURL = (value: string) => `https://${value.trim().replace(/^https?:\/\//i, '')}`

export function WorkspaceOnboarding({ onCreated }: { onCreated: (id: string) => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [url, setURL] = useState('')
  const [created, setCreated] = useState<Created | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const workspace = await api.post<Workspace>('/api/workspaces', { name: name.trim() })
      setActiveWorkspaceRoute(workspace.slug)
      setCreated({ workspace })
      await qc.invalidateQueries({ queryKey: ['me'] })
      onCreated(workspace.id)
      const brand = await api.post<Brand>(`/api/workspaces/${workspace.id}/brands`, {
        name: name.trim(), spoken_name: name.trim(), url: websiteURL(url),
      })
      setCreated({ workspace, brand })
      const task = await api.post<Task>(
        `/api/workspaces/${workspace.id}/brands/${brand.id}/extract-onboarding`,
      )
      return { workspace, brand, task }
    },
    onSuccess: result => {
      setCreated(result)
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })

  const task = useQuery({
    queryKey: ['task', created?.workspace.id, created?.task?.id],
    queryFn: () => api.get<Task>(
      `/api/workspaces/${created!.workspace.id}/tasks/${created!.task!.id}`),
    enabled: !!created?.task,
    refetchInterval: q => {
      const status = (q.state.data as Task | undefined)?.status
      return status === 'done' || status === 'failed' || status === 'cancelled' ? false : 1500
    },
  })

  const continueToReview = () => {
    if (!created) return
    setActiveWorkspaceRoute(created.workspace.slug)
    onCreated(created.workspace.id)
    void qc.invalidateQueries({ queryKey: ['brand', created.workspace.id] })
    go({ name: 'settings', section: 'brands', sub: 'new' })
  }

  useEffect(() => {
    if (task.data?.status === 'done') void continueToReview()
    // Navigation is deliberately tied only to the terminal transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.data?.status])

  if (created) {
    const failed = create.isError || task.isError || task.data?.status === 'failed' || task.data?.status === 'cancelled'
    return (
      <OnboardingFrame>
        <Card className="space-y-5 border-brand/25">
          <div className="flex items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
              {failed ? <Globe className="size-4" /> : <LoaderCircle className="size-4 animate-spin" />}
            </span>
            <div>
              <CardTitle>{failed ? 'Website could not be read' : 'Reading your website'}</CardTitle>
              <p className="mt-1 text-sm text-ink-faint">
                {failed
                  ? 'The workspace is ready. You can enter or edit its brand context manually.'
                  : `Extracting the product, audience and benefits from ${websiteURL(url)}.`}
              </p>
            </div>
          </div>
          {failed && <ErrorText error={create.error || task.error || task.data?.error} />}
          {failed && <Button variant="primary" onClick={continueToReview}>Review brand context</Button>}
          {!failed && <Note tone="brand">This usually takes a minute. The result will open for review before it is used.</Note>}
        </Card>
      </OnboardingFrame>
    )
  }

  return (
    <OnboardingFrame>
      <button
        type="button" onClick={() => go({ name: 'jobs' })}
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Back
      </button>
      <div className="mb-7 max-w-xl">
        <h1 className="text-3xl font-semibold tracking-tight">Create a workspace</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-faint">
          One workspace holds one business. We’ll read its website, then let you review the scripting context before it is used.
        </p>
      </div>
      <Card className="space-y-5">
        <div className="flex items-center gap-2">
          <Building2 className="size-4 text-brand" />
          <CardTitle>Business</CardTitle>
        </div>
        <Field label="Business name">
          <Input autoFocus value={name} onChange={e => setName(e.target.value)} maxLength={80} placeholder="ContentStudio" />
        </Field>
        <Field label="Website URL" help="Used to draft brand context. You will review it before use.">
          <div className="flex h-9.5 overflow-hidden rounded-[--radius-control] border border-line bg-surface-2 transition-colors focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/25">
            <span className="flex items-center border-r border-line bg-surface-3/50 px-3 text-sm text-ink-faint">
              https://
            </span>
            <Input
              value={url}
              onChange={e => setURL(e.target.value.replace(/^https?:\/\//i, ''))}
              placeholder="example.com"
              className="h-full rounded-none border-0 bg-transparent focus:ring-0"
            />
          </div>
        </Field>
        <ErrorText error={create.error} />
        <Button
          variant="primary" onClick={() => create.mutate()}
          disabled={!name.trim() || !url.trim() || create.isPending}
        >
          {create.isPending ? 'Creating…' : 'Next'}
        </Button>
      </Card>
    </OnboardingFrame>
  )
}

function OnboardingFrame({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-2xl py-8">{children}</div>
}
